/* ============================================================
   spn-products.js — 商品詳細ページの表示ロジック（Shopify 版）
   移植元: assets/products.js（静的サンプル・2026-05-13 生成版）の表示部分。
   - 商品データは Shopify から渡す（window.SPN_PRODUCTS / window.SPN_VARIANTS ＝ spn-main-product が出力）
   - Shopify が商品データの正本になったため、このファイルは assets/products.js から再生成せず、ここで保守する
   ============================================================ */
(function () {
  "use strict";

  // 商品データ（Liquid で出力）。形は静的サンプルの PRODUCTS / VARIANTS と同じ
  const PRODUCTS = window.SPN_PRODUCTS || [];
  const VARIANTS = window.SPN_VARIANTS || [];

  // ============================================================
  // アクセサヘルパー
  // ============================================================
  function getEnabledProducts() {
    return PRODUCTS.filter(function (p) { return p.enabled; });
  }
  function findProduct(slug) {
    return PRODUCTS.find(function (p) { return p.product_slug === slug; });
  }
  function getVariantsByProduct(slug) {
    return VARIANTS.filter(function (v) { return v.product_slug === slug && v.enabled; });
  }
  function getProductsByCategory(category) {
    return getEnabledProducts().filter(function (p) { return p.category === category; });
  }
  function getMinPrice(slug) {
    const vars = getVariantsByProduct(slug);
    if (vars.length === 0) return null;
    const prices = vars.map(function (v) { return v.price; }).filter(function (p) { return p != null; });
    return prices.length > 0 ? Math.min.apply(null, prices) : null;
  }
  // カテゴリ + (任意の接続タイプ) に該当する有効商品を取得
  function getProductsByCategoryAndConn(cat, conn) {
    var ps = getProductsByCategory(cat);
    if (!conn) return ps;
    return ps.filter(function (p) {
      var vs = getVariantsByProduct(p.product_slug);
      return vs.some(function (v) { return v.connection_type === conn; });
    });
  }
  // カテゴリ・接続タイプのリンク URL を返す。
  // 判定基準は「有効 variant 数」：1 件だけなら商品詳細直リンク、複数なら一覧。
  // → スマスロユニット (8 variant) はメインカテゴリクリックで一覧へ、
  //    トランス (1 variant) は直リンク、接続タイプ指定なら必ず該当 variant に直リンク。
  function getCategoryLinkUrl(cat, conn) {
    if (!cat) return '/collections/all';
    var ps = getProductsByCategoryAndConn(cat, conn);
    var matched = [];
    ps.forEach(function (p) {
      var vs = getVariantsByProduct(p.product_slug);
      vs.forEach(function (v) {
        if (!conn || v.connection_type === conn) matched.push({ p: p, v: v });
      });
    });
    if (matched.length === 1) {
      var hit = matched[0];
      return '/products/' + hit.p.product_slug + (hit.v.connection_type ? '?conn=' + hit.v.connection_type : '');
    }
    return '/collections/' + cat + (conn ? '?conn=' + conn : '');
  }

  // ============================================================
  // レンダラー
  // ============================================================
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (m) {
      return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[m];
    });
  }
  function formatPrice(n) {
    return "¥" + Number(n).toLocaleString("ja-JP");
  }
  const CATEGORY_LABELS = {
    "smaslo-unit":     "スマスロユニット",
    "smapachi-unit":   "スマパチユニット",
    "coinless":        "コイン不要機",
    "auto-play":       "オートプレイユニット",
    "trans":           "トランス",
    "converter":       "イヤホンコンバーター",
    "nav-auto":        "ナビオートユニット",
    "press-auto":      "順押しオートユニット",
    "harness":         "コンバーター用ハーネス",
    "reel-stopper":    "リールストッパー",
    "accessory":       "オプション部品",
    "cr-adapter":      "CRアダプター"
  };
  const CATEGORY_BADGES = {
    "smaslo-unit":     { label: "スマスロ",       cls: "smart" },
    "smapachi-unit":   { label: "スマパチ",       cls: "pachi" },
    "coinless":        { label: "コインレス",     cls: "smart" },
    "auto-play":       { label: "オート",         cls: "smart" },
    "trans":           { label: "トランス",       cls: "smart" },
    "converter":       { label: "コンバーター",   cls: "smart" },
    "nav-auto":        { label: "ナビオート",     cls: "smart" },
    "press-auto":      { label: "順押しオート",   cls: "smart" },
    "cr-adapter":      { label: "パチンコ",       cls: "pachi" }
  };

  // 商品カード 1 枚を HTML 文字列で返す（collection.html / 横断利用）
  function renderProductCard(p) {
    const minPrice = getMinPrice(p.product_slug);
    const variants = getVariantsByProduct(p.product_slug);
    const hasMultiple = variants.length > 1;
    const inStock = variants.some(function (v) { return v.stock_status === "在庫あり"; });
    const badge = CATEGORY_BADGES[p.category];
    const catLabel = CATEGORY_LABELS[p.category] || p.category;
    // product.image_url 未設定なら、有効な variant の最初の画像にフォールバック
    const firstVariantWithImage = variants.find(function (v) { return v.image_url; });
    const cardImage = p.image_url || (firstVariantWithImage ? firstVariantWithImage.image_url : '');
    let html = '<a class="product-card" href="/products/' + escapeHtml(p.product_slug) + '">';
    html += '<div class="thumb">';
    html += '<div class="badges">';
    if (p.featured) html += '<span class="badge new">NEW</span>';
    if (badge) html += '<span class="badge ' + badge.cls + '">' + escapeHtml(badge.label) + '</span>';
    html += '</div>';
    if (cardImage) {
      html += '<img src="' + escapeHtml(cardImage) + '" alt="' + escapeHtml(p.name) + '" loading="lazy">';
    } else {
      html += '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="12" rx="1.5"/><path d="M7 10v4M12 10v4M17 10v4"/></svg>';
    }
    html += '</div>';
    html += '<div class="body">';
    html += '<div class="cat">' + escapeHtml(catLabel) + '</div>';
    html += '<div class="title">' + escapeHtml(p.name) + '</div>';
    if (p.description_short) {
      html += '<div class="compat">' + escapeHtml(p.description_short) + '</div>';
    }
    html += '<div class="price-row">';
    if (minPrice != null) {
      html += '<div class="price">' + formatPrice(minPrice) + (hasMultiple ? '〜' : '') + '<small>(税込)</small></div>';
    } else {
      html += '<div class="price">価格未定</div>';
    }
    if (inStock) {
      html += '<span class="stock-pill">在庫あり</span>';
    } else if (variants.length > 0) {
      html += '<span class="stock-pill out">取り寄せ</span>';
    }
    html += '</div></div></a>';
    return html;
  }

  // 商品（variant）カード 1 枚 — カテゴリ一覧で variant 単位で並べる用
  function renderVariantCard(p, v, opts) {
    opts = opts || {};
    const badge = CATEGORY_BADGES[p.category];
    const catLabel = CATEGORY_LABELS[p.category] || p.category;
    const inStock = v.stock_status === '在庫あり';
    let href = '/products/' + escapeHtml(p.product_slug) + '?' + (v.connection_type ? 'conn=' + escapeHtml(v.connection_type) : 'variant=' + v.id);
    // 機種ページから呼び出された場合、メーカー・機種名を URL に乗せて商品ページのフォームを事前入力
    //  - smaslo-unit / smapachi-unit: ?maker= で直結プルダウン用に予約
    //  - coinless: ?prefillMaker=&prefillMachine= で フォームに直接入力
    if (opts.maker) {
      if (p.category === 'coinless') {
        href += '&prefillMaker=' + encodeURIComponent(opts.maker);
        if (opts.machineName) href += '&prefillMachine=' + encodeURIComponent(opts.machineName);
      } else if (p.category === 'smaslo-unit' || p.category === 'smapachi-unit') {
        href += '&maker=' + encodeURIComponent(opts.maker);
        // メイン基板接続タイプの機種名欄にも自動入力する（2026-10-19）
        if (opts.machineName) href += '&prefillMachine=' + encodeURIComponent(opts.machineName);
      }
    }
    let html = '<a class="product-card" href="' + href + '">';
    html += '<div class="thumb">';
    html += '<div class="badges">';
    if (p.featured) html += '<span class="badge new">NEW</span>';
    if (badge) html += '<span class="badge ' + badge.cls + '">' + escapeHtml(badge.label) + '</span>';
    html += '</div>';
    const imgUrl = v.image_url || p.image_url;
    if (imgUrl) {
      html += '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(v.variant_label || p.name) + '" loading="lazy">';
    } else {
      html += '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="12" rx="1.5"/><path d="M7 10v4M12 10v4M17 10v4"/></svg>';
    }
    html += '</div>';
    html += '<div class="body">';
    html += '<div class="cat">' + escapeHtml(catLabel) + '</div>';
    html += '<div class="title">' + escapeHtml(v.variant_label || p.name) + '</div>';
    if (p.description_short) {
      html += '<div class="compat">' + escapeHtml(p.description_short) + '</div>';
    }
    html += '<div class="price-row">';
    if (v.price != null) {
      html += '<div class="price">' + formatPrice(v.price) + '<small>(税込)</small></div>';
    } else {
      html += '<div class="price">価格未定</div>';
    }
    if (inStock) {
      html += '<span class="stock-pill">在庫あり</span>';
    } else {
      html += '<span class="stock-pill out">取り寄せ</span>';
    }
    html += '</div></div></a>';
    return html;
  }

  // 商品グリッド：data-category 属性 or ?cat= URL パラメータでカテゴリ絞り込み
  // カテゴリ指定時は variant ごとに並べる（スマスロユニット → 8 variant カード）
  // ?conn= 付きなら該当 variant のみに絞り込み
  // 公開商品 0 件の場合は既存の HTML フォールバックを保持（innerHTML 上書きしない）
  function renderProductGrid(target) {
    let cat = target.getAttribute("data-category");
    const params = new URLSearchParams(location.search);
    if (!cat) cat = params.get("cat");
    const connFilter = params.get("conn");
    // URL の ?maker= / ?prefillMaker= / ?prefillMachine= を variant カードに引き継ぐ
    // （機種ページ → collection.html を経由しても、商品ページへの遷移時にメーカー情報が保持される）
    const carriedMaker = params.get("maker") || params.get("prefillMaker") || "";
    const carriedMachine = params.get("prefillMachine") || "";
    const variantOpts = carriedMaker ? { maker: carriedMaker, machineName: carriedMachine } : null;
    const products = cat ? getProductsByCategory(cat) : getEnabledProducts();
    if (products.length === 0) return;
    let html = '';
    products.forEach(function (p) {
      const vs = getVariantsByProduct(p.product_slug);
      if (vs.length === 0) return;
      if (cat && vs.length > 1) {
        const list = connFilter ? vs.filter(function (v) { return v.connection_type === connFilter; }) : vs;
        list.forEach(function (v) { html += renderVariantCard(p, v, variantOpts); });
      } else {
        html += renderProductCard(p);
      }
    });
    if (html) target.innerHTML = html;
  }

  // カートボタン（[data-add-to-cart] 全部）の無効化。理由ごとに管理し、全理由が解消したら有効化
  function setCartBlocked(reason, blocked) {
    document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      const reasons = (btn.dataset.cartBlockedBy || '').split(' ').filter(Boolean).filter(function (r) { return r !== reason; });
      if (blocked) reasons.push(reason);
      // disabled 属性は付けない（押しても無反応になり「ボタンが効かない」と感じるため。2026-10-15）。
      // 押されたら spn-product-cart.js が足りない入力を案内してその欄へスクロールする
      if (reasons.length) {
        btn.dataset.cartBlockedBy = reasons.join(' ');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        delete btn.dataset.cartBlockedBy;
        btn.removeAttribute('aria-disabled');
        const err = document.querySelector('[data-cart-error]');
        if (err) err.hidden = true;   // 入力がそろったら案内を消す
        document.querySelectorAll('[data-cart-guide]').forEach(function (g) { g.remove(); });
      }
    });
  }

  // 商品ヒーロー：window.SPN_CURRENT_SLUG（Liquid が出力）の商品詳細を描画
  // 該当商品なしの場合は既存の HTML を保持
  function renderProductDetail(target) {
    const params = new URLSearchParams(location.search);
    const slug = window.SPN_CURRENT_SLUG;
    if (!slug) return;
    const p = findProduct(slug);
    if (!p || !p.enabled) return;
    const variants = getVariantsByProduct(p.product_slug);
    const connParam = params.get("conn");
    // conn URL パラメータに合致する variant があれば、それを「選択中」として扱う
    const variantParam = params.get("variant");
    const selectedVariant = connParam ? variants.find(function (v) { return v.connection_type === connParam; })
      : variantParam ? variants.find(function (v) { return String(v.id) === variantParam; }) : null;
    const minPrice = getMinPrice(p.product_slug);
    const displayPrice = selectedVariant && selectedVariant.price != null ? selectedVariant.price : minPrice;
    const showPriceRange = !selectedVariant && variants.length > 1;
    const badge = CATEGORY_BADGES[p.category];
    const catLabel = CATEGORY_LABELS[p.category] || p.category;
    // document.title はサーバー側の SEO タイトル（data/seo.json）をそのまま使う（上書きしない。2026-10-16 fusion 指摘）
    // breadcrumb の最後を商品名で書き換え
    const bc = document.querySelector('[data-render="product-breadcrumb-name"]');
    if (bc) bc.textContent = p.name;
    // ギャラリーメイン画像（variant 選択時は variant 画像を優先、未指定なら有効 variant の最初の画像にフォールバック）
    const gallery = document.querySelector('[data-render="product-gallery-main"]');
    const firstVariantWithImage = variants.find(function (v) { return v.image_url; });
    const imgUrl = (selectedVariant && selectedVariant.image_url) || p.image_url || (firstVariantWithImage ? firstVariantWithImage.image_url : '');
    if (gallery && imgUrl) {
      gallery.innerHTML = '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(p.name + (selectedVariant ? ' ' + (selectedVariant.variant_label || '') : '')) + '" loading="lazy">';
    }
    // category リンクの handle を書き換え
    const catLink = document.querySelector('[data-render="product-breadcrumb-category"]');
    if (catLink) {
      catLink.textContent = catLabel;
      catLink.setAttribute('href', getCategoryLinkUrl(p.category));
    }
    let html = '<div class="label-line">';
    if (badge) html += '<span class="pill ' + badge.cls + '">' + escapeHtml(badge.label) + '対応</span>';
    if (p.featured) html += '<span class="pill new">NEW</span>';
    html += '<span class="pill cat">' + escapeHtml(catLabel) + '</span>';
    html += '</div>';
    html += '<div><h1>' + escapeHtml(p.name) + '</h1>';
    html += '</div>';
    html += '<div class="price-block"><div class="price">' + (displayPrice != null ? formatPrice(displayPrice) + (showPriceRange ? '〜' : '') : '価格未定') + '<span class="price-tax"> (税込)</span></div>';
    html += '<div class="ship-info"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="13" height="12" rx="1"/><path d="M16 10h4l-1 4h-3"/><circle cx="8" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/></svg>全商品 送料無料</div>';
    html += '</div>';
    // オート系（nav-auto / press-auto）は単体で動作せず、本体ベース機が必要。
    // 価格表示直下、variant 選択前に「必要な本体」を明示する。
    if (p.category === 'nav-auto' || p.category === 'press-auto') {
      html += '<div class="requires-base-notice">';
      html += '<div class="rbn-head">';
      html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 2 22h20z"/><path d="M12 9v5M12 18v.01"/></svg>';
      html += '<strong>本商品は単体では動作しません</strong>';
      html += '</div>';
      html += '<p class="rbn-lead">オートユニットは本体ユニットの拡張パーツです。お持ちの遊技機タイプに応じて、以下の本体を別途ご用意ください。</p>';
      html += '<div class="rbn-grid">';
      html += '<a class="rbn-card" href="/products/abm-su2">';
      html += '<div class="rbn-type">スマスロ機種の場合</div>';
      html += '<div class="rbn-base">スマスロユニット 本体 →</div>';
      html += '</a>';
      html += '<a class="rbn-card" href="/collections/coinless">';
      html += '<div class="rbn-type">通常パチスロ機種の場合</div>';
      html += '<div class="rbn-base">コイン不要機 本体 →</div>';
      html += '</a>';
      html += '</div>';
      html += '</div>';
    }
    if (variants.length > 1) {
      // nav-auto / press-auto は variant 自体が対応機種カタログ。
      // ラベルを「対応機種を選ぶ」に切替えて主役感を出す。
      const isMachineCatalog = (p.category === 'nav-auto' || p.category === 'press-auto');
      const selectLabel = isMachineCatalog
        ? '対応機種を選ぶ（全 ' + variants.length + ' 機種）'
        : 'タイプを選ぶ';
      // variant 変更で URL ?conn= を更新（メーカー指定はクリア、選択し直しを促す）
      html += '<div class="variant-select"><label>' + selectLabel + '</label>';
      html += '<select data-variant-select data-product-slug="' + escapeHtml(p.product_slug) + '">';
      if (!selectedVariant) html += '<option value="" selected disabled>選んでください</option>';
      variants.forEach(function (v) {
        const isSelected = selectedVariant && v.sku === selectedVariant.sku;
        html += '<option value="' + escapeHtml(v.sku) + '" data-variant-id="' + escapeHtml(v.id) + '" data-conn="' + escapeHtml(v.connection_type || '') + '"' + (isSelected ? ' selected' : '') + '>' + escapeHtml(v.variant_label || v.sku) + ' — ' + formatPrice(v.price) + '</option>';
      });
      html += '</select></div>';
      // 対応機種カタログ系：「対応していない機種は LINE で相談」案内
      if (isMachineCatalog) {
        html += '<p class="variant-catalog-note">※ 上記リストにない機種は現在対応していません。';
        html += '<a href="#" data-line="free">LINE で機種名をお送りください</a>。';
        html += '今後の対応可否をスタッフが個別にご案内します。</p>';
      }
    }
    // 機種名・メーカー入力 UI：メイン基板接続 タイプ選択時のみ表示
    //  - 機種名は必須（未入力ならカートに入れない）。メーカーは任意（2026-10-20）
    //  - BET / D-SUB（USB 給電） / box / smakey はメーカー問わず使えるため、安心案内バッジを代わりに表示
    const makerParam = params.get('maker');
    const needsMaker = selectedVariant && NEEDS_MAKER_CONNS.indexOf(selectedVariant.connection_type) !== -1;
    const isUnitNonDirectVariant = (p.category === 'smaslo-unit' || p.category === 'smapachi-unit')
      && selectedVariant && !needsMaker;
    if (isUnitNonDirectVariant) {
      // 「メーカー問わず使えます」安心案内
      html += '<div class="universal-fit-notice">';
      html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 4 4 10-10"/></svg>';
      html += '<div class="ufn-text">';
      html += '<strong>メーカー選択は不要です</strong>';
      html += '<p>本タイプは ' + (p.category === 'smapachi-unit' ? 'スマパチ機' : 'スマスロ機') + ' であれば、どのメーカー・どの機種でもそのままお使いいただけます。差込むだけで動作し、メーカー別の専用部品も必要ありません。</p>';
      html += '</div>';
      html += '</div>';
    }
    if (needsMaker) {
      // 2026-10-20 ユーザー決定：必須は機種名だけ。メーカーは「わかれば」（分からなければ当店で調べる）。
      // 機種名を打つと機種一覧（spn-machines-data.js）から候補を出し、選ぶとメーカーも自動で入る。
      // どのメーカーの機種でもメイン基板接続で対応できる（ユーザー確認）ので、対応外として止めることはしない
      const makerList = unitMakerList(p.category);
      const selectedMakerName = makerList.find(function (m) { return m.slug === makerParam; });
      html += '<div class="maker-select">';
      html += '<label class="maker-select-label" for="spn-unit-machine"><strong>① 機種名をご入力ください（必須）</strong></label>';
      html += '<div class="unit-machine-field">';
      html += '<input type="text" id="spn-unit-machine" class="unit-machine-input" data-unit-machine autocomplete="off" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="spn-unit-machine-suggest" placeholder="例：スマスロ北斗の拳、モンキーターン">';
      html += '<ul class="unit-machine-suggest" id="spn-unit-machine-suggest" role="listbox" data-unit-machine-suggest hidden></ul>';
      html += '</div>';
      html += '<p class="unit-machine-hint">' + (window.SPN_MACHINE_DATA ? '入力すると候補が出ます。選ぶとメーカーも自動で入ります。候補に無い機種も' : '') +
        'そのまま入力して大丈夫です（筐体上部のロゴ名などでも可）。</p>';
      html += '<p class="unit-machine-restored" data-unit-machine-restored hidden>前に入力した機種名を表示しています。違う機種の場合は書き換えてください。</p>';
      html += '<label class="maker-select-label" for="spn-unit-maker"><strong>② メーカー（わかれば）</strong></label>';
      html += '<select id="spn-unit-maker" data-maker-select>';
      html += '<option value="">わからない（当店でお調べします）</option>';
      makerList.forEach(function (m) {
        const isSel = makerParam === m.slug;
        html += '<option value="' + escapeHtml(m.slug) + '"' + (isSel ? ' selected' : '') + '>' + escapeHtml(m.name) + '</option>';
      });
      html += '</select>';
      html += '<div class="maker-select-confirm" data-maker-confirm>';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 4 4 10-10"/></svg>';
      html += '<span data-maker-confirm-text>' + makerConfirmText(selectedMakerName) + '</span>';
      html += '</div>';
      html += '</div>';
    }
    // コイン不要機 専用入力フォーム：メーカー + 機種名
    //  - sessionStorage に保存（key: 'slopachi:coinless:{slug}'）して同セッション内で値を保持
    //  - 全 2 項目入力済みまで [data-add-to-cart] を無効化
    //  - 入力内容のサマリーをリアルタイム表示（誤注文防止）
    // coinless_mode = 'machine-only'：メーカー別の商品（「サミー用」等）。メーカーは商品で
    // 決まっているので選ばせず、機種名だけ入力してもらう（2026-10-13 ユーザー決定）
    const coinlessMachineOnly = p.coinless_mode === 'machine-only';
    if (p.category === 'coinless') {
      html += '<div class="coinless-form" data-coinless-form>';
      if (!coinlessMachineOnly) {
        // ステップ 1：メーカー
        html += '<div class="cf-step">';
        html += '<div class="cf-step-head"><span class="cf-step-num">1</span><strong>メーカーをお選びください</strong></div>';
        html += '<select class="cf-maker-select" data-cf-maker>';
        html += '<option value="">メーカーを選んでください</option>';
        COINLESS_MAKER_LIST.forEach(function (m) {
          html += '<option value="' + escapeHtml(m.slug) + '">' + escapeHtml(m.name) + '</option>';
        });
        html += '</select>';
        html += '<p class="cf-hint">メーカーが分からない方は <a href="#" data-line="free">LINE で機種の写真をお送りください</a>。スタッフが無料でお調べします。</p>';
        html += '</div>';
      }
      // ステップ 2：機種名
      html += '<div class="cf-step">';
      html += '<div class="cf-step-head"><span class="cf-step-num">' + (coinlessMachineOnly ? '1' : '2') + '</span><strong>機種名をご入力ください</strong></div>';
      html += '<input type="text" class="cf-machine-input" data-cf-machine placeholder="例：ハナハナ鳳凰、ジャグラーガールズ、バジリスク絆2 など">';
      html += '<p class="cf-hint">正式名称が分からなくても、リールに書かれている文字や筐体上部のロゴ名でも大丈夫です。<br>同名でも年代によって配線が異なる場合があります。型番（例：BC-○○）が分かる場合は併記してください。</p>';
      html += '</div>';
      // 確認サマリー（入力中はプレースホルダ、3 項目埋まると正式表示）
      html += '<div class="cf-summary" data-cf-summary aria-live="polite">';
      html += '<div class="cf-summary-head"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16v.01"/></svg><strong>ご注文前にもう一度ご確認ください</strong></div>';
      html += '<div class="cf-summary-body" data-cf-summary-body><p class="cf-summary-empty">' +
        (coinlessMachineOnly ? '↑ 機種名をご入力ください。' : '↑ 上の項目をご入力ください。') +
        '入力した内容がこちらに表示されます。</p></div>';
      html += '<label class="cf-confirm"><input type="checkbox" data-cf-confirm disabled> <span>上記内容で間違いありません</span></label>';
      html += '</div>';
      // 注意書き
      html += '<div class="cf-warning">';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><path d="M12 9v4M12 17v.01"/></svg>';
      html += '<div><strong>入力内容に基づき配線を選定します</strong><p>入力誤りによる動作不良は返品対象外となります。ご不明な場合は <a href="#" data-line="free">LINE で写真をお送りください</a>。</p></div>';
      html += '</div>';
      html += '</div>';
    }
    // データ出力あり/なし の variant を持つカテゴリ（スマスロ/スマパチユニット）には、
    // タイプ選択の混乱を避けるための簡潔説明 + 注意喚起を variant 選択直下に常時表示。
    if (p.category === 'smaslo-unit' || p.category === 'smapachi-unit') {
      html += '<div class="data-output-notice">';
      html += '<div class="don-head">';
      html += '<svg class="don-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16v.01"/></svg>';
      html += '<strong>「データ出力あり／なし」の違い</strong>';
      html += '</div>';
      html += '<table class="don-table"><tr><th></th><th>データ出力あり</th><th>データ出力なし</th></tr>';
      html += '<tr><td>データカウンター接続</td><td class="don-yes">✓ 可能</td><td class="don-no">✗ 不可</td></tr>';
      html += '<tr><td>出玉・差枚の記録</td><td class="don-yes">✓ 可能</td><td class="don-no">✗ 不可</td></tr>';
      html += '</table>';
      html += '<p class="don-warn"><strong>ご注文前にご確認ください：</strong>データカウンターをご使用予定の方は必ず「<strong>データ出力あり</strong>」をお選びください。「<strong>データ出力なし</strong>」のユニットには、データカウンターを接続することができません。</p>';
      html += '</div>';
    }
    // クレジット投入方法 案内（smaslo-unit / smapachi-unit のみ、variant 選択時に動的表示）
    if ((p.category === 'smaslo-unit' || p.category === 'smapachi-unit') && selectedVariant) {
      const creditInfo = CREDIT_INFO_BY_CONN[selectedVariant.connection_type];
      if (creditInfo) {
        // スマパチは 250（D-SUB接続 / ボックスタイプのみ 125）、スマスロは 50（2026-10-14 ユーザー確認）
        const creditAmount = p.category === 'smapachi-unit'
          ? (selectedVariant.connection_type === 'box' ? '125' : '250')
          : '50';
        // モード別アイコン: auto=チェック、manual-only=指マーク、manual-auto=切替矢印
        const modeIcon = creditInfo.mode === 'auto'
          ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.1"/><path d="m9 11 3 3 7-7"/></svg>'
          : creditInfo.mode === 'manual-auto'
            ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h11l-3-3M17 17H6l3 3"/></svg>'
            : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M9 12h6"/></svg>';
        html += '<div class="credit-info-block credit-mode-' + creditInfo.mode + '">';
        html += '<div class="cib-head">' + modeIcon + '<strong>クレジット投入方法：' + escapeHtml(creditInfo.label) + '</strong></div>';
        html += '<p class="cib-detail">' + escapeHtml(creditInfo.detail) + '</p>';
        html += '<p class="cib-amount">1 回の投入で <strong>' + creditAmount + ' クレジット</strong></p>';
        html += '</div>';
      }
    }
    if (p.description_long) {
      html += '<div class="product-summary-desc">' + escapeHtml(p.description_long).replace(/\n/g, '<br>') + '</div>';
    }
    target.innerHTML = html;
    // variant select / maker select の change イベントを bind（inline onchange のエスケープを避けるため外出し）
    const variantSelect = target.querySelector('[data-variant-select]');
    if (variantSelect) {
      variantSelect.addEventListener('change', function () {
        const opt = this.options[this.selectedIndex];
        const conn = opt.getAttribute('data-conn') || '';
        const u = new URL(location.href);
        // 接続タイプがある商品は ?conn=、無い商品（ナビオート等）は ?variant= で選択状態を持つ
        if (conn) { u.searchParams.set('conn', conn); u.searchParams.delete('variant'); }
        else { u.searchParams.set('variant', opt.getAttribute('data-variant-id') || ''); u.searchParams.delete('conn'); }
        // maker パラメータは保持（機種ページからの自動入力 + ユーザーが選んだメーカーを variant 切替時に残すため）
        location.href = u.toString();
      });
    }
    const makerSelect = target.querySelector('[data-maker-select]');
    // メーカーは任意（2026-10-20）。選び直してもページは読み直さず、URL（?maker=）・確認文・同梱物の表示だけ更新する
    const applyMaker = function () {
      if (!makerSelect) return;
      const u = new URL(location.href);
      if (makerSelect.value) u.searchParams.set('maker', makerSelect.value); else u.searchParams.delete('maker');
      history.replaceState(null, '', u.toString());
      const m = unitMakerList(p.category).find(function (x) { return x.slug === makerSelect.value; });
      const txt = target.querySelector('[data-maker-confirm-text]');
      if (txt) txt.innerHTML = makerConfirmText(m);
      // 同梱物「機種別専用ハーネス ×1（○○ 用）」は商品説明タブにある
      document.querySelectorAll('[data-render="product-tab-desc"]').forEach(renderProductTabDesc);
      document.querySelectorAll('[data-render="product-tab-spec"]').forEach(renderProductTabSpec);
    };
    if (makerSelect) makerSelect.addEventListener('change', applyMaker);
    setCartBlocked('maker', false);
    // 機種名（メイン基板接続タイプ）：未入力ならカートに入れない。メーカー選択でページを読み直しても消えないよう
    // sessionStorage に保存。機種ページからは ?prefillMachine= で自動入力（2026-10-19）
    const unitMachineInput = target.querySelector('[data-unit-machine]');
    if (unitMachineInput) {
      const unitKey = 'slopachi:unit-machine:' + p.product_slug;
      const prefillUnitMachine = params.get('prefillMachine');
      let savedUnitMachine = '';
      try { savedUnitMachine = sessionStorage.getItem(unitKey) || ''; } catch (e) { savedUnitMachine = ''; }
      unitMachineInput.value = prefillUnitMachine || savedUnitMachine;
      // 前回の入力を復元したときは、別の台の注文で取り違えないよう一言出す（機種ページからの自動入力では出さない）
      const restoredNote = target.querySelector('[data-unit-machine-restored]');
      if (restoredNote && !prefillUnitMachine && savedUnitMachine) restoredNote.hidden = false;
      const syncUnitMachine = function () {
        const v = unitMachineInput.value.trim();
        try { if (v) sessionStorage.setItem(unitKey, v); else sessionStorage.removeItem(unitKey); } catch (e) { /* 保存できなくても入力は使える */ }
        setCartBlocked('unit-machine', !v);
      };
      if (prefillUnitMachine) {
        const cleanU = new URL(location.href);
        cleanU.searchParams.delete('prefillMachine');
        history.replaceState(null, '', cleanU.toString());
      }
      unitMachineInput.addEventListener('input', syncUnitMachine);
      syncUnitMachine();
      // 機種名の候補（2026-10-20）：選ぶと機種名とメーカーが入る
      const suggest = target.querySelector('[data-unit-machine-suggest]');
      // 候補（または機種ページ）で自動的に入れたメーカー。機種名を書き換えたら外す（自分で選んだメーカーは残す。Codex 指摘）
      // 読み直し・タイプ変更をまたいでも分かるよう sessionStorage に残す（Codex 指摘 v2）
      const autoKey = 'slopachi:unit-automaker:' + p.product_slug;
      const saveAuto = function (v) {
        try { if (v) sessionStorage.setItem(autoKey, JSON.stringify(v)); else sessionStorage.removeItem(autoKey); } catch (e) { /* 保存できなくても動く */ }
      };
      let autoMaker = null;
      if (prefillUnitMachine && makerParam) {
        autoMaker = { name: prefillUnitMachine, maker: makerParam };
      } else {
        try { autoMaker = JSON.parse(sessionStorage.getItem(autoKey) || 'null'); } catch (e) { autoMaker = null; }
        // 今の機種名・メーカーと合わない記録は使わない
        if (autoMaker && (autoMaker.name !== unitMachineInput.value.trim() || !makerSelect || makerSelect.value !== autoMaker.maker)) autoMaker = null;
      }
      saveAuto(autoMaker);
      if (makerSelect) makerSelect.addEventListener('change', function () { autoMaker = null; saveAuto(null); });
      unitMachineInput.addEventListener('input', function () {
        if (!autoMaker || unitMachineInput.value.trim() === autoMaker.name) return;
        if (makerSelect && makerSelect.value === autoMaker.maker) { makerSelect.value = ''; applyMaker(); }
        autoMaker = null;
        saveAuto(null);
      });
      if (suggest) {
        let closeTimer = null;
        const closeSuggest = function () { suggest.hidden = true; unitMachineInput.setAttribute('aria-expanded', 'false'); };
        const showSuggest = function () {
          const r = unitMachineCandidates(p.category, unitMachineInput.value, 8);
          if (!r.list.length) { closeSuggest(); return; }
          suggest.innerHTML = r.list.map(function (m) {
            return '<li role="option"><button type="button" data-slug="' + escapeHtml(m.slug) + '">' + escapeHtml(m.name) +
              (m.makerName ? '<small>（' + escapeHtml(m.makerName) + '）</small>' : '') + '</button></li>';
          }).join('') + (r.more > 0 ? '<li class="unit-machine-suggest-more">ほか ' + r.more + ' 件。続けて入力すると絞り込めます。</li>' : '');
          suggest.hidden = false;
          unitMachineInput.setAttribute('aria-expanded', 'true');
        };
        const pick = function (slug) {
          const m = ((window.SPN_MACHINE_DATA && window.SPN_MACHINE_DATA.machines) || []).find(function (x) { return x.slug === slug; });
          if (!m) return;
          unitMachineInput.value = m.name;
          syncUnitMachine();
          if (restoredNote) restoredNote.hidden = true;
          if (makerSelect && [].some.call(makerSelect.options, function (o) { return o.value === m.maker; })) {
            makerSelect.value = m.maker;
            applyMaker();
            autoMaker = { name: m.name, maker: m.maker };
            saveAuto(autoMaker);
          }
          closeSuggest();
        };
        unitMachineInput.addEventListener('input', showSuggest);
        unitMachineInput.addEventListener('focus', function () { clearTimeout(closeTimer); if (unitMachineInput.value.trim()) showSuggest(); });
        unitMachineInput.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSuggest(); });
        // 入力欄から候補ボタンへ移るとき（Tab など）は閉じない。それ以外で離れたら閉じる
        const closeIfOutside = function (e) {
          const next = e.relatedTarget;
          if (next && (next === unitMachineInput || suggest.contains(next))) return;
          clearTimeout(closeTimer);
          closeTimer = setTimeout(closeSuggest, 200);
        };
        unitMachineInput.addEventListener('blur', closeIfOutside);
        suggest.addEventListener('focusout', closeIfOutside);
        suggest.addEventListener('focusin', function () { clearTimeout(closeTimer); });
        // 指・マウスで押した瞬間に確定（入力欄のフォーカスが外れて一覧が消える前に）。キーボードは click で確定
        suggest.addEventListener('mousedown', function (e) {
          const b = e.target.closest('button[data-slug]');
          e.preventDefault();
          if (b) pick(b.dataset.slug);
        });
        suggest.addEventListener('click', function (e) {
          const b = e.target.closest('button[data-slug]');
          if (b && !suggest.hidden) pick(b.dataset.slug);
        });
      }
    } else {
      setCartBlocked('unit-machine', false);
    }
    // 複数タイプの商品でタイプ未選択のときもカートに入れられない
    setCartBlocked('variant', variants.length > 1 && !selectedVariant);
    // コイン不要機 専用フォームの初期化（sessionStorage 復元 + URL prefill + 入力イベント bind + カート制御）
    if (p.category === 'coinless') {
      const storeKey = 'slopachi:coinless:' + p.product_slug;
      let saved = {};
      try { saved = JSON.parse(sessionStorage.getItem(storeKey) || '{}'); } catch (e) { saved = {}; }
      // URL ?prefillMaker= / ?prefillMachine= が指定されていれば優先（機種ページからの自動入力遷移用）
      const prefillMaker = params.get('prefillMaker');
      const prefillMachine = params.get('prefillMachine');
      if (prefillMaker)   saved.maker = prefillMaker;
      if (prefillMachine) saved.machine = prefillMachine;
      const form = target.querySelector('[data-coinless-form]');
      const makerInput = form ? form.querySelector('[data-cf-maker]') : null;
      const machineInput = form ? form.querySelector('[data-cf-machine]') : null;
      const summaryBody = form ? form.querySelector('[data-cf-summary-body]') : null;
      const confirmCheck = form ? form.querySelector('[data-cf-confirm]') : null;
      // 保存値を UI に復元
      if (saved.maker && makerInput) makerInput.value = saved.maker;
      if (saved.machine && machineInput) machineInput.value = saved.machine;
      // prefill が効いた場合は URL から削除（ブラウザバック・リロード時に再適用されないように）
      if (prefillMaker || prefillMachine) {
        const cleanU = new URL(location.href);
        cleanU.searchParams.delete('prefillMaker');
        cleanU.searchParams.delete('prefillMachine');
        history.replaceState(null, '', cleanU.toString());
      }
      function readForm() {
        return {
          maker:   makerInput ? makerInput.value : '',
          machine: machineInput ? (machineInput.value || '').trim() : ''
        };
      }
      function makerLabel(slug) {
        const m = COINLESS_MAKER_LIST.find(function (x) { return x.slug === slug; });
        return m ? m.name : '';
      }
      function updateSummary() {
        const s = readForm();
        // 保存できない環境（プライベートブラウズ等）でも入力・カート判定は続ける
        try { sessionStorage.setItem(storeKey, JSON.stringify(s)); } catch (e) { /* 保存しないだけ */ }
        const allFilled = !!((coinlessMachineOnly || s.maker) && s.machine);
        if (summaryBody) {
          if (allFilled) {
            summaryBody.innerHTML =
              '<dl class="cf-summary-list">' +
              (coinlessMachineOnly
                ? '<dt>商品</dt><dd>' + escapeHtml(p.name) + '</dd>'
                : '<dt>メーカー</dt><dd>' + escapeHtml(makerLabel(s.maker)) + '</dd>') +
              '<dt>機種名</dt><dd>' + escapeHtml(s.machine) + '</dd>' +
              '</dl>';
          } else {
            const missing = [];
            if (!coinlessMachineOnly && !s.maker) missing.push('メーカー');
            if (!s.machine) missing.push('機種名');
            summaryBody.innerHTML = '<p class="cf-summary-empty">未入力：<strong>' + escapeHtml(missing.join('・')) + '</strong></p>';
          }
        }
        if (confirmCheck) {
          confirmCheck.disabled = !allFilled;
          if (!allFilled) confirmCheck.checked = false;
        }
        updateCoinlessCart(allFilled && confirmCheck && confirmCheck.checked);
      }
      function updateCoinlessCart(ok) {
        setCartBlocked('coinless-form', !ok);
      }
      if (makerInput)   makerInput.addEventListener('change', updateSummary);
      if (machineInput) machineInput.addEventListener('input',  updateSummary);
      if (confirmCheck) confirmCheck.addEventListener('change', updateSummary);
      updateSummary();
    }
  }

  // 機種ページの「対応ユニット」を商品データから描画（machine_type に応じて smaslo/smapachi のユニット商品を抽出）
  function renderMachineCompatProducts(target) {
    const machineType = target.getAttribute("data-machine-type");
    const cat = machineType === "smapachi" ? "smapachi-unit" : "smaslo-unit";
    const products = getProductsByCategory(cat);
    if (products.length === 0) return;
    target.innerHTML = products.map(renderProductCard).join("");
  }

  // ?slug= で現在の商品を取得（enabled でなければ null）
  function getCurrentProduct() {
    const slug = window.SPN_CURRENT_SLUG;
    if (!slug) return null;
    const p = findProduct(slug);
    return (p && p.enabled) ? p : null;
  }

  // 機種互換性 UI が不要なカテゴリ (万能電源など)。
  // ここで列挙したカテゴリの商品ページでは spec-table / line-consult-box / ハーネス注意書きを取り除く。
  const MACHINE_COMPAT_SKIP_CATEGORIES = ['trans'];

  // 商品カテゴリ → 対応機種タイプの machine_type 群（spec-table の機種・メーカー集計に使用）
  const CATEGORY_MACHINE_TYPES = {
    'smaslo-unit':     ['smaslo'],
    'smapachi-unit':   ['smapachi'],
    'coinless':        ['standard-slot'],
    'auto-play':       ['smaslo', 'standard-slot'],
    'nav-auto':        ['smaslo', 'standard-slot'],
    'press-auto':      ['smaslo', 'standard-slot']
  };

  // 機種別ハーネスが付属する接続タイプ。
  // メイン基板接続 タイプのみメーカー別ハーネスを付属（機種固有配線が必須）。
  // BETボタン / D-SUB接続 / ボックス / スマキー はメーカー問わず汎用接続でハーネス無し。
  const HARNESS_CONNECTION_TYPES = ['main-no-data', 'main-with-data'];
  // メーカー選択が必要な接続タイプ（メイン基板接続のみ）。
  // 接続するメーカーごとにハーネス形状が違うため、購入時に選んでもらう必要がある。
  const NEEDS_MAKER_CONNS = ['main-no-data', 'main-with-data'];
  // 接続タイプ別のクレジット投入方法。
  // mode: 'auto' / 'manual-only' / 'manual-auto'。商品ページに動的表示。
  const CREDIT_INFO_BY_CONN = {
    'main-no-data':   { mode: 'auto',         label: '自動投入のみ',         detail: 'クレジットが 0 になると自動的に補充されます。ボタン操作は不要で、遊技に集中できます。' },
    'main-with-data': { mode: 'auto',         label: '自動投入のみ',         detail: 'クレジットが 0 になると自動的に補充されます。ボタン操作は不要で、遊技に集中できます。' },
    'bet-no-data':    { mode: 'manual-auto',  label: '手動投入 + 自動切替可', detail: 'BET ボタンを押すとクレジットが入ります。BET ボタンを長押しすると、自動投入モードに切り替えられます。お好みで使い分けできます。' },
    'bet-with-data':  { mode: 'manual-auto',  label: '手動投入 + 自動切替可', detail: 'BET ボタンを押すとクレジットが入ります。BET ボタンを長押しすると、自動投入モードに切り替えられます。お好みで使い分けできます。' },
    'usb-no-data':    { mode: 'auto',         label: '自動投入のみ',         detail: 'クレジットが 0 になると自動的に補充されます。ボタン操作は不要で、遊技に集中できます。' },
    'usb-with-data':  { mode: 'auto',         label: '自動投入のみ',         detail: 'クレジットが 0 になると自動的に補充されます。ボタン操作は不要で、遊技に集中できます。' },
    'box':            { mode: 'manual-auto',  label: '手動投入 + 自動切替可', detail: 'ボックスのボタンを押すとクレジットが入ります。長押しまたは自動ボタンの操作で、自動投入モードに切り替えられます。' },
    'smakey':         { mode: 'manual-only',  label: '手動投入',             detail: 'スマキー本体のボタンを押すと、クレジットが入ります。電池式で配線不要。倉庫などで機械の動作チェックをする方に人気の商品です。' }
  };
  // 直結 / BET タイプの対応メーカーリスト（カテゴリ別）。
  // ユーザー要件：スマスロ 17 メーカー / スマパチ 11 メーカー。
  // メイン基板接続の機種名・メーカー入力で使う（2026-10-20）
  function unitMachineType(category) { return category === 'smapachi-unit' ? 'smapachi' : 'smaslo'; }
  // メーカーの選択肢：主要メーカー（DIRECT_MAKER_LISTS の順）＋ 機種一覧にあるその種類の全メーカー
  function unitMakerList(category) {
    const data = window.SPN_MACHINE_DATA;
    const base = (DIRECT_MAKER_LISTS[category] || []).slice();
    if (!data || !data.machines) return base;
    const type = unitMachineType(category);
    const seen = {};
    base.forEach(function (m) { seen[m.slug] = true; });
    const extra = [];
    data.machines.forEach(function (m) {
      if (!m.enabled || m.machine_type !== type || seen[m.maker]) return;
      seen[m.maker] = true;
      extra.push({ slug: m.maker, name: (data.makers[m.maker] && data.makers[m.maker].name) || m.maker });
    });
    extra.sort(function (a, b) { return a.name.localeCompare(b.name, 'ja'); });
    return base.concat(extra);
  }
  function compactText(s) {
    return String(s || '').normalize('NFKC').toLowerCase().replace(/[\s・:：\-‐－~〜!！?？'’"”.,、。]/g, '');
  }
  // 機種名の候補：その種類（スマスロ／スマパチ）の機種から、名前・読み・ローマ字・別名に部分一致するもの
  // 戻り値 { list: 先頭 limit 件, more: 残りの件数 }。名前の完全一致 → 前方一致 → 名前に含む → 読み・別名に含む の順
  function unitMachineCandidates(category, query, limit) {
    const data = window.SPN_MACHINE_DATA;
    const q = compactText(query);
    if (!data || !data.machines || !q) return { list: [], more: 0 };
    const type = unitMachineType(category);
    const strip = function (n) { return compactText(String(n).replace(/^(スマスロ|スマパチ|Lパチスロ|パチスロ|ぱちスロ)\s*/, '')); };
    const hits = [];
    data.machines.forEach(function (m, idx) {
      if (!m.enabled || m.machine_type !== type) return;
      const full = compactText(m.name), core = strip(m.name);
      let score;
      if (full === q || core === q) score = 0;
      else if (full.indexOf(q) === 0 || core.indexOf(q) === 0) score = 1;
      else if (full.indexOf(q) !== -1) score = 2;
      else if (compactText((m.search_index || '') + ' ' + (m.aliases || '')).indexOf(q) !== -1) score = 3;
      else return;
      hits.push({ score: score, idx: idx, m: m });
    });
    hits.sort(function (a, b) { return a.score - b.score || a.idx - b.idx; });
    return {
      list: hits.slice(0, limit).map(function (h) {
        return { slug: h.m.slug, name: h.m.name, makerName: (data.makers[h.m.maker] && data.makers[h.m.maker].name) || '' };
      }),
      more: Math.max(0, hits.length - limit),
    };
  }
  function makerConfirmText(m) {
    return m
      ? '<strong>' + escapeHtml(m.name) + '</strong> の機種に合うハーネスを同梱してお届けします。'
      : 'ご入力の機種名から、当店で合うハーネスを確認して同梱します。';
  }
  const DIRECT_MAKER_LISTS = {
    'smaslo-unit': [
      { slug: 'universal-bros', name: 'ユニバーサル' },
      { slug: 'sammy',          name: 'サミー' },
      { slug: 'sankyo',         name: '三共' },
      { slug: 'yamasa',         name: '山佐' },
      { slug: 'daito',          name: '大都' },
      { slug: 'fujishoji',      name: '藤商事' },
      { slug: 'excite',         name: 'EXCITE' },
      { slug: 'olympia',        name: 'オリンピア' },
      { slug: 'kitadenshi',     name: '北電子' },
      { slug: 'enterrise',      name: 'エンターライズ' },
      { slug: 'oizumi',         name: 'オーイズミ' },
      { slug: 'kpe',            name: 'KPE' },
      { slug: 'kyoraku',        name: '京楽' },
      { slug: 'ef',             name: 'エフ' },
      { slug: 'net',            name: 'ネット' },
      { slug: 'pioneer',        name: 'パイオニア' },
      { slug: 'sanyo',          name: '三洋' }
    ],
    'smapachi-unit': [
      { slug: 'sankyo',         name: '三共' },
      { slug: 'heiwa',          name: '平和' },
      { slug: 'sammy',          name: 'サミー' },
      { slug: 'newgin',         name: 'ニューギン' },
      { slug: 'kyoraku',        name: '京楽' },
      { slug: 'fujishoji',      name: '藤商事' },
      { slug: 'nanasho',        name: '七匠' },
      { slug: 'daiichi',        name: '大一' },
      { slug: 'daito',          name: '大都' },
      { slug: 'sansei-rd',      name: 'サンセイ' },
      { slug: 'universal-bros', name: 'ユニバーサル' }
    ]
  };
  // コイン不要機 用：対応メーカーリスト（スマスロ以外の旧基板〜新基板パチスロ向け）
  // スマスロより範囲が広く、廃業メーカーや 4 号機時代の機種も含む
  const COINLESS_MAKER_LIST = [
    { slug: 'sammy',          name: 'サミー' },
    { slug: 'yamasa',         name: '山佐' },
    { slug: 'kitadenshi',     name: '北電子（ジャグラー）' },
    { slug: 'daito',          name: '大都技研' },
    { slug: 'kpe',            name: 'KPE' },
    { slug: 'universal-bros', name: 'ユニバーサル' },
    { slug: 'olympia',        name: 'オリンピア' },
    { slug: 'enterrise',      name: 'エンターライズ' },
    { slug: 'pioneer',        name: 'パイオニア' },
    { slug: 'kyoraku',        name: '京楽' },
    { slug: 'sankyo',         name: '三共' },
    { slug: 'net',            name: 'ネット' },
    { slug: 'oizumi',         name: 'オーイズミ' },
    { slug: 'rodeo',          name: 'ロデオ' },
    { slug: 'eleco',          name: 'エレコ' },
    { slug: 'meshi',          name: 'メーシー' },
    { slug: 'takasago',       name: '高砂電器' },
    { slug: 'belco',          name: 'ベルコ' },
    { slug: 'ginza',          name: '銀座' },
    { slug: 'other',          name: 'その他・分からない' }
  ];
  function variantNeedsHarness(v) {
    return !!(v && HARNESS_CONNECTION_TYPES.indexOf(v.connection_type) !== -1);
  }
  // 商品が「ハーネス付属しうる variant」を 1 つでも持つか（spec-table の無 variant 時表示用）
  function productHasAnyHarnessVariant(slug) {
    var vs = getVariantsByProduct(slug);
    return vs.some(variantNeedsHarness);
  }
  // 接続タイプ別の同梱物リスト（variant が選択されている時は p.bundle ではなくこちらを表示）
  const BUNDLE_BY_CONNECTION = {
    'main-no-data':   ['本体ユニット ×1', '機種別専用ハーネス ×1', '取扱説明書 ×1'],
    'main-with-data': ['本体ユニット ×1', '機種別専用ハーネス ×1', '取扱説明書 ×1'],
    'bet-no-data':    ['本体ユニット ×1', '接続コード ×1', '取扱説明書 ×1'],
    'bet-with-data':  ['本体ユニット ×1', '接続コード ×1', '取扱説明書 ×1'],
    'usb-no-data':    ['本体ユニット ×1', 'USBケーブル ×1', 'USB電源アダプター ×1', '取扱説明書 ×1'],
    'usb-with-data':  ['本体ユニット ×1', 'USBケーブル ×1', 'USB電源アダプター ×1', '取扱説明書 ×1'],
    'box':            ['本体ユニット ×1', '接続コード ×1', 'USBケーブル ×1', 'USB電源アダプター ×1', '取扱説明書 ×1'],
    'smakey':         ['本体ユニット ×1', '電池（CR2032）×1', '取扱説明書 ×1']
  };

  // product.html の spec-table を商品データから描画
  function renderProductSpecTable(target) {
    const p = getCurrentProduct();
    if (!p) return;
    // 互換性 UI 不要カテゴリは spec-table 自体を取り除く
    if (MACHINE_COMPAT_SKIP_CATEGORIES.indexOf(p.category) !== -1) {
      if (target.parentNode) target.parentNode.removeChild(target);
      return;
    }
    const machinesList = (window.SLOPACHI_MACHINES && window.SLOPACHI_MACHINES.list) || [];
    const allowedTypes = CATEGORY_MACHINE_TYPES[p.category] || null;
    function categoryFilter(m) {
      if (!m.enabled) return false;
      if (!allowedTypes) return true;
      const t = m.machine_type || 'smaslo';
      return allowedTypes.indexOf(t) !== -1;
    }
    // 対応機種：カテゴリの machine_type で絞り込み
    let machinesHtml;
    if (p.compat_machines === "ALL" || p.compat_machines === "ALL_SLOT") {
      const filtered = machinesList.filter(categoryFilter);
      machinesHtml = filtered.length > 0
        ? '<span>全 ' + filtered.length + ' 機種対応</span>'
        : '<span style="color: var(--text-mute);">対応機種準備中</span>';
    } else if (p.compat_machines) {
      const slugs = p.compat_machines.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
      machinesHtml = '<div class="compat-list">' + slugs.map(function (s) {
        const m = machinesList.find(function (x) { return x.slug === s; });
        const nm = m ? m.name : s;
        return '<a class="chip" href="/pages/machine/' + escapeHtml(s) + '">' + escapeHtml(nm) + '</a>';
      }).join('') + '<a class="chip" href="/search">+ 全ての対応機種を見る</a></div>';
    } else {
      machinesHtml = '<span>—</span>';
    }
    // 対応メーカー：カテゴリの machine_type を持つ機種から実在メーカーを集計
    const makersData = (window.SLOPACHI_MACHINES && window.SLOPACHI_MACHINES.makers) || {};
    let makersHtml;
    if (p.compat_makers === "ALL") {
      const makerSet = {};
      machinesList.forEach(function (m) {
        if (categoryFilter(m)) makerSet[m.maker] = true;
      });
      const makerCount = Object.keys(makerSet).length;
      makersHtml = makerCount > 0
        ? '全 ' + makerCount + ' メーカー対応'
        : '<span style="color: var(--text-mute);">対応メーカー準備中</span>';
    } else if (p.compat_makers) {
      const slugs = p.compat_makers.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
      makersHtml = slugs.map(function (s) { return escapeHtml((makersData[s] && makersData[s].name) || s); }).join(' / ');
    } else {
      makersHtml = '—';
    }
    // テーブル HTML（対応可否行は省略 — 商品の存在自体が対応の証）
    // 対応機種 / 対応メーカー 行は、その商品が「特定タイプの機種に紐づく」場合のみ表示。
    //  - converter: 通常パチスロ系は数値が誤解を招くため非表示
    //  - nav-auto / press-auto: variant 自体が対応機種カタログのため、全機種カウントは実態と乖離する
    //  - coinless: 機種名は注文時にお客様が入力するため、汎用「対応機種」行は不要
    const COMPAT_INFO_SKIP_CATEGORIES = ['converter', 'nav-auto', 'press-auto', 'coinless', 'cr-adapter'];
    const skipCompatInfo = COMPAT_INFO_SKIP_CATEGORIES.indexOf(p.category) !== -1;
    // 必要ハーネス行を非表示にするカテゴリ。
    //  - nav-auto / press-auto: 本体にハーネス内蔵のため「機種別ハーネス不要」表記が無意味
    const HARNESS_ROW_SKIP_CATEGORIES = ['nav-auto', 'press-auto', 'cr-adapter'];
    const skipHarnessRow = HARNESS_ROW_SKIP_CATEGORIES.indexOf(p.category) !== -1;
    let html = '';
    if (!skipCompatInfo) {
      html += '<tr><th>対応機種</th><td>' + machinesHtml + '</td></tr>';
      html += '<tr><th>対応メーカー</th><td>' + makersHtml + '</td></tr>';
    }
    // 必要ハーネス：選択中 variant の connection_type で動的判定
    const connSel = (new URLSearchParams(location.search)).get('conn');
    const variantsSel = getVariantsByProduct(p.product_slug);
    const selV = connSel ? variantsSel.find(function (v) { return v.connection_type === connSel; }) : null;
    let harnessRow;
    if (p.category === 'coinless') {
      // コイン不要機：ハーネスを本体に取り付け済の状態で出荷するのが特徴のためメッセージを強調表示
      harnessRow = 'お選びいただいた機種に合わせて、当店で専用ハーネス（配線）を本体ユニットに取り付けた状態でお届けします。お客様側では本体を実機に差し込むだけ、配線知識は不要です。';
    } else if (selV) {
      harnessRow = variantNeedsHarness(selV)
        ? '<small style="color: var(--text-mute);">選択機種に応じて専用ハーネスを付属します。</small>'
        : '機種別ハーネス不要・単体で動作';
    } else if (productHasAnyHarnessVariant(p.product_slug)) {
      harnessRow = '<small style="color: var(--text-mute);">メイン基板接続タイプは機種別ハーネス付属。D-SUB接続・ボックス・スマキー はハーネス不要。</small>';
    } else if (p.required_harness) {
      harnessRow = '<small style="color: var(--text-mute);">選択機種に応じて専用ハーネスを付属します。</small>';
    } else {
      harnessRow = '機種別ハーネス不要・単体で動作';
    }
    if (!skipHarnessRow) {
      html += '<tr><th>必要ハーネス</th><td>' + harnessRow + '</td></tr>';
    }
    // 2026-10-13 ユーザー指示：「13:00 までのご注文で当日発送」→「翌日発送」
    html += '<tr><th>発送目安</th><td><strong>翌日発送</strong>（土日祝除く。銀行振込・コンビニ払いはお支払い確認の翌日）</td></tr>';
    target.innerHTML = html;
  }

  // LINE 相談ボタンの data-product-code / data-product-name を動的更新
  function renderProductLineConsult(target) {
    const p = getCurrentProduct();
    if (!p) return;
    // 互換性 UI 不要カテゴリは LINE 相談ボックスごと取り除く
    if (MACHINE_COMPAT_SKIP_CATEGORIES.indexOf(p.category) !== -1) {
      const box = target.closest('.line-consult-box');
      if (box && box.parentNode) box.parentNode.removeChild(box);
      return;
    }
    const variants = getVariantsByProduct(p.product_slug);
    // 選んだタイプ（?conn= / ?variant=）の品番を送る。未選択なら 1 つ目（2026-10-16 fusion 指摘）
    const qs = new URLSearchParams(location.search);
    // 商品表示と同じく conn を優先し、無いときだけ variant で探す
    const picked = qs.get('conn') ? variants.find(function (v) { return v.connection_type === qs.get('conn'); })
      : qs.get('variant') ? variants.find(function (v) { return String(v.id) === qs.get('variant'); }) : null;
    const first = picked || variants[0];
    const code = first && first.sku ? first.sku : p.product_slug;
    target.setAttribute('data-product-code', code);
    target.setAttribute('data-product-name', p.name);
    if (window.LINE && typeof window.LINE.rebind === 'function') window.LINE.rebind();
  }

  // 商品説明タブ：description_long + features + bundle
  function renderProductTabDesc(target) {
    const p = getCurrentProduct();
    if (!p) return;
    let html = '';
    if (p.description_long) {
      html += '<h3>商品概要</h3>';
      html += '<p>' + escapeHtml(p.description_long).replace(/\n/g, '<br>') + '</p>';
    }
    if (p.features && p.features.length > 0) {
      html += '<h3>主な特徴</h3><ul>';
      p.features.forEach(function (f) { html += '<li>' + escapeHtml(f) + '</li>'; });
      html += '</ul>';
    }
    // クレジット投入方法 比較カード（smaslo-unit / smapachi-unit のみ）
    if (p.category === 'smaslo-unit' || p.category === 'smapachi-unit') {
      const amt = p.category === 'smapachi-unit' ? '250' : '50';
      html += '<h3>クレジットの入れ方</h3>';
      html += '<p>接続タイプによってクレジットの入れ方が変わります。ご自身に合うタイプをお選びください。</p>';
      html += '<div class="credit-compare-list">';
      // モード別カードデータ：[mode, アイコン記号, タイプ名, 投入方法, 切替方法（任意）]
      const cards = [
        { mode: 'auto',        name: 'D-SUB接続 / メイン基板接続', method: '自動投入のみ',            sub: 'クレジットが 0 になると自動的に補充されます。ボタン操作は不要です。', modes: ['auto'] },
        // 2026-10-14 一旦非表示（ユーザー指示）: BET接続タイプの販売停止に合わせて案内も出さない
        // { mode: 'manual-auto', name: 'BET接続',                method: 'BET ボタンで投入',         sub: 'BET ボタンを長押しすると自動投入モードに切り替えられます。',          modes: ['auto', 'manual'] },
        { mode: 'manual-auto', name: 'D-SUB接続 / ボックスタイプ',          method: 'ボックスのボタンで投入',    sub: '長押しまたは自動ボタンで、自動投入モードに切り替えられます。',         modes: ['auto', 'manual'] },
        { mode: 'manual-only', name: 'D-SUB接続 / スマキー',                method: '本体ボタンで投入',          sub: 'スマキー本体のボタンを押すたびにクレジットが入ります。',               modes: ['manual'] }
      ];
      cards.forEach(function (c) {
        html += '<div class="credit-row credit-row-' + c.mode + '">';
        html += '<div class="credit-row-head">';
        html += '<span class="credit-dot"></span>';
        html += '<span class="credit-conn-name">' + c.name + '</span>';
        html += '<span class="credit-modes">';
        c.modes.forEach(function (m) {
          html += '<span class="credit-mode-badge ' + m + '">' + (m === 'auto' ? '自動投入' : '手動投入') + '</span>';
        });
        html += '</span>';
        html += '</div>';
        html += '<div class="credit-row-method">' + c.method + '</div>';
        html += '<div class="credit-row-sub">' + c.sub + '</div>';
        html += '</div>';
      });
      html += '</div>';
      const amtNote = p.category === 'smapachi-unit' ? '（ボックスタイプのみ <strong>125 クレジット</strong>）' : '';
      html += '<p style="font-size: 15px; color: var(--text-2); margin-top: 10px;">※ 1 回の投入で <strong>' + amt + ' クレジット</strong>' + amtNote + 'が入ります。自動投入時もこの枚数で補充されます。</p>';
    }
    // コイン不要機 専用：使い方 + オートベット切替 + 注意書きカード
    if (p.category === 'coinless') {
      // 使い方カード
      html += '<h3>使い方は 2 ステップだけ</h3>';
      html += '<div class="usage-steps">';
      html += '<div class="usage-step"><span class="usage-step-num">1</span><div class="usage-step-body"><strong>本体を実機に接続する</strong><p>当店からお届けする本体とハーネス（配線）を、実機の所定の位置に差し込みます。配線はハーネスに合わせて設計されているため、迷わず取り付けできます。</p></div></div>';
      html += '<div class="usage-step"><span class="usage-step-num">2</span><div class="usage-step-body"><strong>ボタンを押してクレジットを入れる</strong><p>実機の電源を入れた状態で、<strong>「1BETボタン」または「精算ボタン」</strong>を押すと、クレジットが一度に満タンまで入ります。クレジットがなくなったら、もう一度同じボタンを押すだけで再び満タンに戻ります。何度でも繰り返しお使いいただけます。</p></div></div>';
      html += '</div>';
      // ボタンの種類について
      html += '<div class="info-card info-card-cyan">';
      html += '<div class="info-card-head"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16v.01"/></svg><strong>どちらのボタンを押せばいいですか？</strong></div>';
      html += '<p>押すボタンは、接続する機種によって以下のいずれかになります。</p>';
      html += '<ul class="info-card-list"><li>「1BETボタン」で操作する機種</li><li>「精算ボタン」で操作する機種</li></ul>';
      html += '<p>お客様の機種にどちらが対応しているかは、当店からお届けする取扱説明書に記載しております。ご不明な場合は、<a href="#" data-line="free">LINE</a> でお気軽にお問い合わせください。</p>';
      html += '</div>';
      // オートベット切替カード
      html += '<h3>オートベットの ON / OFF 切り替え</h3>';
      html += '<p>ボタンを押さなくても、自動でベット（賭け）が行われる便利な機能です。ON にすると遊技がよりスムーズになります。お好みで ON / OFF を切り替えてご利用ください。</p>';
      html += '<div class="autobet-steps">';
      html += '<div class="autobet-step"><span class="ab-num">1</span><div><strong>実機の電源を一度 OFF にします</strong></div></div>';
      html += '<div class="autobet-step"><span class="ab-num">2</span><div><strong>「1BETボタン」または「精算ボタン」を押したまま、実機の電源を ON にします</strong></div></div>';
      html += '<div class="autobet-step"><span class="ab-num">3</span><div><strong>切り替えが完了します（オートベットあり ⇔ オートベットなし）</strong></div></div>';
      html += '</div>';
      html += '<p>もう一度同じ操作（電源 OFF → ボタンを押しながら電源 ON）を行うと、元の状態に戻ります。つまり、同じ操作で<strong>「オートベットあり」</strong>と<strong>「オートベットなし」</strong>を、お好みに合わせて何度でも切り替えできます。</p>';
      html += '<div class="info-card info-card-cyan">';
      html += '<div class="info-card-head"><strong>切替のコツ</strong></div>';
      html += '<ul class="info-card-list"><li>電源を入れる<strong>「前」</strong>からボタンを押し始めるのがコツです</li><li>電源 ON の瞬間にボタンが押されていることが必要です</li><li>押すボタンはクレジット投入と同じ（機種により 1BET または 精算）</li></ul>';
      html += '</div>';
      // 注意書きカード
      html += '<h3>ご使用前にお読みください</h3>';
      html += '<div class="caution-card">';
      html += '<ul class="caution-list">';
      html += '<li><strong>操作に使うボタンは機種によって異なります。</strong>「1BETボタン」で操作する機種と「精算ボタン」で操作する機種があります。お客様の機種に対応するボタンは、同梱の取扱説明書でご案内しております。</li>';
      html += '<li><strong>オートベットの切り替えは「電源を入れる瞬間」にボタンが押されている必要があります。</strong>タイミングがずれると切り替わらない場合がありますので、ボタンを押した状態から電源を入れてください。</li>';
      html += '<li><strong>本機はクレジットを「満タンまで一度に補充」する仕様です。</strong>1枚ずつや少量の投入はできません。</li>';
      html += '<li><strong>取付時は必ず実機の電源を OFF にしてから作業してください。</strong>通電中の作業は故障の原因となります。</li>';
      html += '<li><strong>操作しても反応がない場合は、無理に繰り返し操作しないでください。</strong><a href="#" data-line="free">LINE</a> で写真を添えてご相談いただければ、スタッフが状況を確認のうえご案内いたします。</li>';
      html += '<li><strong>改造や他社製ハーネスとの併用は動作保証外となります。</strong>あらかじめご了承ください。</li>';
      html += '</ul>';
      html += '</div>';
    }
    // 同梱物：選択中 variant の connection_type に応じて専用リストを表示
    //  - BUNDLE_BY_CONNECTION に定義がある variant は接続タイプ別リストで上書き
    //  - 定義がない場合や variant 未選択時は p.bundle（汎用）を使用
    const connSelD = (new URLSearchParams(location.search)).get('conn');
    const variantsD = getVariantsByProduct(p.product_slug);
    const selVD = connSelD ? variantsD.find(function (v) { return v.connection_type === connSelD; }) : null;
    const variantBundle = selVD && BUNDLE_BY_CONNECTION[selVD.connection_type];
    // 直結 / BET タイプかつメーカー選択済の場合、機種別専用ハーネスにメーカー名を併記
    const makerParamD = (new URLSearchParams(location.search)).get('maker');
    const needsMakerD = selVD && NEEDS_MAKER_CONNS.indexOf(selVD.connection_type) !== -1;
    const makerListD = unitMakerList(p.category);
    const selectedMakerD = needsMakerD && makerParamD ? makerListD.find(function (m) { return m.slug === makerParamD; }) : null;
    const bundleList = (variantBundle || p.bundle || []).map(function (b) {
      if (selectedMakerD && b.indexOf('機種別専用ハーネス') !== -1) {
        return '機種別専用ハーネス ×1（' + selectedMakerD.name + ' 用）';
      }
      return b;
    });
    if (bundleList && bundleList.length > 0) {
      html += '<h3>同梱物</h3><ul>';
      bundleList.forEach(function (b) { html += '<li>' + escapeHtml(b) + '</li>'; });
      html += '</ul>';
      // 補足注釈：variant 未選択の場合のみ、接続タイプにより内容が異なる旨を明示
      if (!selVD && Object.keys(BUNDLE_BY_CONNECTION).some(function (ct) {
        return variantsD.some(function (v) { return v.connection_type === ct; });
      })) {
        html += '<p style="color: var(--text-mute);">※ 同梱物は選択する接続タイプにより異なります。タイプ選択後の商品ページで詳細をご確認ください。</p>';
      }
    }
    // 対応機種の情報（スマスロ／スマパチユニット。build-shopify-products-csv.js が機種データから作り、文字は作成時にエスケープ済み。2026-10-28）
    if (p.detail_html) html += p.detail_html;
    target.innerHTML = html;
  }

  // 詳細スペックタブ：SKU 一覧 + vendor + spec_text 解析
  function renderProductTabSpec(target) {
    const p = getCurrentProduct();
    if (!p) return;
    const variants = getVariantsByProduct(p.product_slug);
    let html = '<h3>詳細スペック</h3>';
    html += '<table class="doc-table">';
    if (variants.length === 1) {
      html += '<tr><th>商品型番</th><td>' + escapeHtml(variants[0].sku) + '</td></tr>';
    } else if (variants.length > 1) {
      html += '<tr><th>商品型番</th><td>' + variants.map(function (v) {
        return escapeHtml(v.sku) + (v.variant_label ? '（' + escapeHtml(v.variant_label) + '）' : '');
      }).join('<br>') + '</td></tr>';
    }
    if (p.spec_text) {
      const items = p.spec_text.split('|').map(function (s) { return s.trim(); }).filter(Boolean);
      items.forEach(function (item) {
        const idx = item.indexOf(':');
        if (idx > 0) {
          html += '<tr><th>' + escapeHtml(item.substring(0, idx).trim()) + '</th><td>' + escapeHtml(item.substring(idx + 1).trim()) + '</td></tr>';
        } else {
          html += '<tr><td colspan="2">' + escapeHtml(item) + '</td></tr>';
        }
      });
    }
    html += '</table>';
    target.innerHTML = html;
  }

  // ============================================================
  // マウント
  // ============================================================
  function mountAll() {
    document.querySelectorAll('[data-render="product-grid"]').forEach(renderProductGrid);
    document.querySelectorAll('[data-render="product-detail"]').forEach(renderProductDetail);
    document.querySelectorAll('[data-render="product-spec-table"]').forEach(renderProductSpecTable);
    document.querySelectorAll('[data-render="product-line-consult"]').forEach(renderProductLineConsult);
    document.querySelectorAll('[data-render="product-tab-desc"]').forEach(renderProductTabDesc);
    document.querySelectorAll('[data-render="product-tab-spec"]').forEach(renderProductTabSpec);
    document.querySelectorAll('[data-render="machine-compat-products"]').forEach(renderMachineCompatProducts);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountAll);
  } else {
    mountAll();
  }

  // ============================================================
  // グローバル公開（HTML から使用）
  // ============================================================
  window.SPN_PRODUCT_PAGE = {
    products:                     PRODUCTS,
    variants:                     VARIANTS,
    getEnabledProducts:           getEnabledProducts,
    findProduct:                  findProduct,
    getVariantsByProduct:         getVariantsByProduct,
    getProductsByCategory:        getProductsByCategory,
    getProductsByCategoryAndConn: getProductsByCategoryAndConn,
    getCategoryLinkUrl:           getCategoryLinkUrl,
    getMinPrice:                  getMinPrice,
    renderProductCard:            renderProductCard,
    renderVariantCard:            renderVariantCard,
    renderProductGrid:            renderProductGrid,
    renderProductDetail:          renderProductDetail
  };
})();
